import { Fragment } from "react";
import { Icon } from "./Icons";

type OperationsRow = [string, string, string, string, string, boolean?];

// A small product-UI mock: a Coprocessor module screenshot with operations table.
// Mimics the structure of the real product UI (grey chrome, orange trial banner,
// monospace headers, red trendline). Labeled as a mock so it's not mistaken for
// the real thing.
export function OperationsMock({
  minimal = false,
}: {
  minimal?: boolean;
}) {
  const rows: OperationsRow[] = [
    ["pressure-smooth",   "Running",  "1 / 1",    "0.4", "11:45:12"],
    ["zone-drift",        "Stopped",  "0 / 0",    "-",   "-"],
    ["bearing-hpf",       "Running",  "10 / 10", "28.4", "11:48:11", true],
    ["pump-cavitation",   "Stopped",  "0 / 0",    "-",   "-"],
  ];

  const stats: Array<[string, string, string]> = [
    ["PROCESSORS", "4", "11 inst"],
    ["RUNNING", "2", ""],
    ["FAULTED", "0", ""],
    ["DEGRADED", "0", ""],
    ["STOPPED", "2", ""],
    ["ENGINE MEMORY", "43%", "0.86 / 2.0 GB"],
  ];

  return (
    <div className="sshot">
      <div className="sshot-chrome">
        <div className="sshot-chrome-title">Coprocessor</div>
        <div className="sshot-tabs">
          <span className="active">Operations</span>
          <span>Engineering</span>
          <span>License</span>
        </div>
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            gap: 12,
            alignItems: "center",
            color: "var(--ink-3)",
            fontSize: 12,
          }}
        >
          <span className="mono">local-dev</span>
          <Icon.Gear style={{ color: "var(--ink-3)" }} />
        </div>
      </div>
      {!minimal && (
        <div className="sshot-trial">
          Trial mode - processors stop in 1h 35m&nbsp;·&nbsp;<a href="#">Open License page</a>
        </div>
      )}
      {/* stat strip */}
      <div
        className="mock-stats"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(6, 1fr) 1.4fr",
          borderBottom: "1px solid var(--line)",
          fontSize: 11,
        }}
      >
        {stats.map(([k, v, s], i) => (
          <div key={i} style={{ padding: "10px 14px", borderRight: "1px solid var(--line)" }}>
            <div className="mono" style={{ color: "var(--ink-3)", fontSize: 10, letterSpacing: "0.08em" }}>
              {k}
            </div>
            <div className="mono tnum" style={{ fontSize: 18, color: "var(--ink)", marginTop: 2 }}>
              {v}
            </div>
            {s && (
              <div className="mono" style={{ color: "var(--ink-4)", fontSize: 10 }}>
                {s}
              </div>
            )}
          </div>
        ))}
        <div style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
          <div className="mono" style={{ color: "var(--ink-3)", fontSize: 10, letterSpacing: "0.08em" }}>
            FLEET · LAST 5M
          </div>
          <svg width="120" height="28" viewBox="0 0 120 28" style={{ flex: 1 }}>
            <polyline
              fill="none"
              stroke="var(--trend)"
              strokeWidth="1.2"
              points="0,22 10,19 20,20 30,14 40,16 50,10 60,12 70,8 80,11 90,6 100,9 110,5 120,7"
            />
          </svg>
        </div>
      </div>
      {/* stacked: list then detail */}
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div style={{ borderBottom: "1px solid var(--line)", background: "#FCFBF5" }}>
          <div
            style={{
              padding: "8px 12px",
              display: "flex",
              gap: 12,
              alignItems: "center",
              borderBottom: "1px solid var(--line)",
              fontSize: 11,
              color: "var(--ink-3)",
            }}
          >
            {[
              { label: "All 4",    active: true  },
              { label: "Faulted 0", active: false },
              { label: "Running 2", active: false },
              { label: "Stopped 2", active: false },
            ].map(({ label, active }) => (
              <span
                key={label}
                style={{
                  padding: "2px 8px",
                  borderRadius: 4,
                  fontSize: 11,
                  cursor: "default",
                  background: active ? "var(--bg-card)" : "transparent",
                  border: "1px solid var(--line)",
                  color: active ? "var(--ink)" : "var(--ink-3)",
                  fontWeight: active ? 500 : 400,
                }}
              >
                {label}
              </span>
            ))}
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "#fff",
                border: "1px solid var(--line)",
                borderRadius: 4,
                padding: "3px 8px",
                width: 140,
              }}
            >
              <Icon.Search />
              <span style={{ color: "var(--ink-4)", fontSize: 11 }}>Search…</span>
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1.2fr 0.8fr 0.8fr 1fr",
              gap: 8,
              padding: "5px 12px",
              fontSize: 10,
              borderBottom: "1px solid var(--line)",
              color: "var(--ink-4)",
              letterSpacing: "0.06em",
              background: "#F0EEE6",
            }}
            className="mono"
          >
            <span>NAME</span>
            <span>STATUS</span>
            <span style={{ textAlign: "right" }}>INST</span>
            <span style={{ textAlign: "right" }}>MSG/S</span>
            <span style={{ textAlign: "right" }}>UPTIME</span>
          </div>
          {rows.map(([n, st, inst, tp_, up, active], i) => (
            <div
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 1.2fr 0.8fr 0.8fr 1fr",
                gap: 8,
                padding: "8px 12px",
                fontSize: 11,
                borderBottom: "1px solid var(--line)",
                background: active ? "#F4EFD8" : "transparent",
              }}
            >
              <span className="mono" style={{ color: "var(--ink)" }}>
                {n}
              </span>
              <span style={{ color: "var(--ink-3)", display: "flex", alignItems: "center", gap: 5 }}>
                <span
                  style={{
                    display: "inline-block",
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: st === "Running" ? "var(--ok)" : "var(--ink-4)",
                    flexShrink: 0,
                  }}
                />
                {st}
              </span>
              <span className="mono tnum" style={{ color: "var(--ink-3)", textAlign: "right" }}>
                {inst}
              </span>
              <span className="mono tnum" style={{ color: "var(--ink-3)", textAlign: "right" }}>
                {tp_}
              </span>
              <span className="mono tnum" style={{ color: "var(--ink-4)", fontSize: 10, textAlign: "right" }}>
                {up}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// A tag-mapping / authoring mock
export function AuthoringMock({
  compact = false,
}: {
  compact?: boolean;
}) {
  const scriptPane = (
    <div
      style={{
        borderRight: "1px solid var(--line)",
        background: "#FCFBF5",
        padding: "12px 10px",
        fontSize: 12,
      }}
    >
      <div
        className="mono"
        style={{ color: "var(--ink-3)", fontSize: 10, letterSpacing: "0.08em", marginBottom: 6 }}
      >
        SCRIPTS
      </div>
      {[
        "bearing_hpf.sql",
        "pump_cavitation.sql",
        "thermal_drift.sql",
        "valve_stuck.sql",
        "cpu_shared.sql",
      ].map((n, i) => (
        <div
          key={i}
          className="mono"
          style={{
            padding: "4px 8px",
            borderRadius: 3,
            background: i === 0 ? "#F4EFD8" : "transparent",
            color: i === 0 ? "var(--ink)" : "var(--ink-3)",
          }}
        >
          {n}
        </div>
      ))}
    </div>
  );

  return (
    <div className="sshot" >
      <div className="sshot-chrome">
        <div className="sshot-chrome-title">Coprocessor</div>
        <div className="sshot-tabs">
          <span>Operations</span>
          <span className="active">Engineering</span>
          <span>License</span>
        </div>
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            gap: 12,
            alignItems: "center",
            color: "var(--ink-3)",
            fontSize: 12,
          }}
        >
          <span className="mono">local-dev</span>
        </div>
      </div>
      <div
        className={compact ? "mock-authoring mock-authoring-compact" : "mock-authoring"}
        style={{
          display: "grid",
          gridTemplateColumns: compact ? "1fr 240px" : "200px 1fr 260px",
          minHeight: compact ? 210 : 250,
        }}
      >
        {!compact && scriptPane}
        <div style={{ padding: "12px 14px", borderRight: "1px solid var(--line)" }}>
          <div
            className="mono"
            style={{
              color: "var(--ink-3)",
              fontSize: 10,
              letterSpacing: "0.08em",
              marginBottom: 6,
            }}
          >
            bearing_hpf.sql
          </div>
          <pre
            className="sql"
            style={{
              border: "none",
              padding: 0,
              background: "transparent",
              fontSize: compact ? 10.6 : 11.2,
              whiteSpace: "pre-wrap",
              overflow: "hidden",
            }}
          >
            <Fragment>
              <span className="cmt">-- isolate bearing fault frequencies</span>
              {"\n"}
              <span className="kw">CREATE MATERIALIZED VIEW</span> bearing_hpf{" "}
              <span className="kw">AS</span>
              {"\n"}
              <span className="kw">SELECT</span> device_id,
              {"\n"}
              {"       "}channel_id,
              {"\n"}
              {"       "}
              <span className="fn">IIR</span>(amplitude,
              {"\n"}
              {"           "}
              <span className="kw">ARRAY</span>[<span className="num">0.7265</span>, -
              <span className="num">1.4530</span>,{" "}
              <span className="num">0.7265</span>],
              {"\n"}
              {"           "}
              <span className="kw">ARRAY</span>[<span className="num">1.0</span>, -
              <span className="num">1.3725</span>,{" "}
              <span className="num">0.5334</span>]){" "}
              <span className="kw">AS</span> filtered
              {"\n"}
              <span className="kw">FROM</span> vibration_raw;
              {!compact && (
                <Fragment>
                  {"\n"}
                  {"\n"}
                  <span className="cmt">{"-- alert when signal spikes above rolling baseline"}</span>
                  {"\n"}
                  <span className="kw">CREATE MATERIALIZED VIEW</span> bearing_alert{" "}
                  <span className="kw">AS</span>
                  {"\n"}
                  <span className="kw">SELECT</span> device_id, channel_id, filtered
                  {"\n"}
                  <span className="kw">FROM</span> bearing_hpf
                  {"\n"}
                  <span className="kw">WHERE</span> filtered &gt;{" "}
                  {"\n"}
                  {"  "}
                  <span className="fn">MOVING_AVERAGE</span>(filtered,{" "}
                  <span className="num">48</span>) * <span className="num">3.0</span>;
                </Fragment>
              )}
            </Fragment>
          </pre>
        </div>
        <div style={{ padding: "12px 14px", background: "#FCFBF5", fontSize: 11 }}>
          <div
            className="mono"
            style={{
              color: "var(--ink-3)",
              fontSize: 10,
              letterSpacing: "0.08em",
              marginBottom: 8,
            }}
          >
            OUTPUT CATALOG
          </div>
          <div className="mono" style={{ marginBottom: 6 }}>
            bearing_hpf <span style={{ color: "var(--ink-4)" }}>· view</span>
          </div>
          <div
            className="mono"
            style={{ fontSize: 10, color: "var(--ink-3)", marginLeft: 10, marginBottom: 10 }}
          >
            device_id : string
            <br />
            channel_id : string
            <br />
            filtered : double
          </div>
          {!compact && (
            <Fragment>
              <div className="mono" style={{ marginBottom: 6 }}>
                bearing_alert <span style={{ color: "var(--ink-4)" }}>· view</span>
              </div>
              <div
                className="mono"
                style={{ fontSize: 10, color: "var(--ink-3)", marginLeft: 10, marginBottom: 10 }}
              >
                device_id : string
                <br />
                channel_id : string
                <br />
                filtered : double
              </div>
            </Fragment>
          )}
          <div className="chip chip-ok" style={{ marginTop: 8 }}>
            <Icon.Check /> Compiled · 0 errors
          </div>
        </div>
      </div>
    </div>
  );
}

// A tag-mapping mock
export function TagMapMock() {
  const rows: Array<[string, string, string, string, string]> = [
    [
      "vibration_raw.device_id",
      "[default]",
      "Machines/{device_id}/{channel_id}/*",
      "",
      "{device_id}",
    ],
    [
      "vibration_raw.channel_id",
      "[default]",
      "Machines/{device_id}/{channel_id}/*",
      "",
      "{channel_id}",
    ],
    [
      "vibration_raw.amplitude",
      "[default]",
      "Machines/{device_id}/{channel_id}/*  (value)",
      "· Float",
      "",
    ],
  ];

  return (
    <div className="sshot" >
      <div className="sshot-chrome">
        <div className="sshot-chrome-title">Coprocessor</div>
        <div className="sshot-tabs">
          <span>Operations</span>
          <span className="active">Engineering</span>
          <span>License</span>
        </div>
      </div>
      <div style={{ padding: "16px 20px" }}>
        <div
          className="mono"
          style={{
            fontSize: 11,
            color: "var(--ink-3)",
            letterSpacing: "0.08em",
            marginBottom: 12,
          }}
        >
          MAP INPUT STREAM → IGNITION TAGS
        </div>
        {rows.map(([field, prefix, pattern, suffix, highlight], i) => (
          <div
            key={i}
            className="mock-tagmap-row"
            style={{
              display: "grid",
              gridTemplateColumns: "180px minmax(0, 1fr)",
              gap: 12,
              alignItems: "center",
              marginBottom: i < rows.length - 1 ? 8 : 16,
            }}
          >
            <div className="mono" style={{ fontSize: 11 }}>
              {field}
            </div>
            <div
              className="mono"
              style={{
                background: "#FBFAF4",
                border: "1px solid var(--line)",
                padding: "8px 10px",
                borderRadius: 4,
                fontSize: 11,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {prefix && (
                <span style={{ color: "var(--ink-4)" }}>{prefix} </span>
              )}
              {pattern.split(/(\{[^}]+\})/).map((part, j) =>
                part.startsWith("{") ? (
                  <span key={j} style={{ background: part === highlight ? "#FFF3C2" : "transparent" }}>{part}</span>
                ) : (
                  <span key={j} style={{ color: "#6B4E1A" }}>{part}</span>
                )
              )}
              {suffix && (
                <span style={{ color: "var(--ink-3)" }}> {suffix}</span>
              )}
            </div>
          </div>
        ))}
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span className="chip chip-ok">
            <Icon.Check /> 10 tags matched
          </span>
          <span className="chip">per-identity: device_id · 10 instances</span>
          <span
            className="mono"
            style={{ marginLeft: "auto", fontSize: 11, color: "var(--ink-3)" }}
          >
            Smoke test ▸
          </span>
        </div>
      </div>
    </div>
  );
}
